// Squad battle live view — per-venue H2H scoreboard. Polls every 4s while
// the page is visible. "Claim" button per venue triggers the claim endpoint;
// success → /squad/battle/$id/claimed/$venueId.

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import { type BattleDto, battlesApi } from '../../../../api'
import { BackButton, TopBar } from '../../../../components/TopBar'

export const Route = createFileRoute('/squad/battle/$id/')({
  component: BattleLiveRoute,
})

function colorVar(color: string): string {
  if (color === 'pink') return 'var(--pink)'
  if (color === 'lime') return 'var(--lime)'
  if (color === 'teal') return 'var(--teal)'
  if (color === 'orange') return 'var(--orange)'
  return color // assume hex or other CSS color
}

function BattleLiveRoute() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const [battle, setBattle] = useState<BattleDto | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [claiming, setClaiming] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const res = await battlesApi.get(id)
      setBattle(res.battle)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load battle.')
    }
  }, [id])

  useEffect(() => {
    void load()
    const t = setInterval(() => {
      if (typeof document === 'undefined' || !document.hidden) {
        void load()
      }
    }, 4000)
    return () => clearInterval(t)
  }, [load])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 2400)
    return () => clearTimeout(t)
  }, [toast])

  async function handleClaim(venueId: string) {
    setClaiming(venueId)
    try {
      const res = await battlesApi.claim(id, venueId)
      if (res.ok) {
        navigate({
          to: '/squad/battle/$id/claimed/$venueId',
          params: { id, venueId },
        })
      } else if (res.reason === 'insufficient') {
        setToast(`Need more drinks here: you ${res.yourCount} · them ${res.theirCount}`)
        void load()
      } else {
        setToast('Not allowed.')
      }
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Claim failed.')
    } finally {
      setClaiming(null)
    }
  }

  if (error) {
    return (
      <div className="stumble-screen" data-testid="battle-error">
        <TopBar
          left={<BackButton onClick={() => navigate({ to: '/' })} />}
          eyebrow="squad battle"
          title="error"
        />
        <div className="screen-body" style={{ display: 'grid', placeItems: 'center' }}>
          <p className="body-sm" role="alert">
            {error}
          </p>
        </div>
      </div>
    )
  }

  if (!battle) {
    return (
      <div className="stumble-screen" data-testid="battle-loading">
        <TopBar
          left={<BackButton onClick={() => navigate({ to: '/' })} />}
          eyebrow="squad battle"
          title="loading"
        />
        <div className="screen-body" style={{ display: 'grid', placeItems: 'center' }}>
          <p className="body-sm muted">Loading battle…</p>
        </div>
      </div>
    )
  }

  const colorA = colorVar(battle.squadA.color)
  const colorB = colorVar(battle.squadB.color)

  return (
    <div className="stumble-screen" data-testid="battle-live-screen">
      <TopBar
        left={<BackButton onClick={() => navigate({ to: '/' })} />}
        eyebrow="squad battle"
        title="live board"
      />
      <div
        className="screen-body"
        style={{
          gridTemplateRows: 'auto auto 1fr auto',
          gap: 12,
          paddingBottom: 'max(16px, calc(var(--safe-bottom, env(safe-area-inset-bottom)) + 16px))',
        }}
      >
        {/* Banner: TEAM A vs TEAM B */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <div
            className="card"
            style={{ background: colorA, color: 'var(--ink)', padding: '10px 12px' }}
          >
            <div className="eyebrow" style={{ color: 'inherit' }}>
              {battle.totals.claimsA} claims
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, lineHeight: 1 }}>
              {battle.squadA.name}
            </div>
            <div className="body-sm">{battle.totals.drinksA} drinks</div>
          </div>
          <div className="vs-badge" style={{ width: 44, height: 44 }}>
            vs
          </div>
          <div
            className="card"
            style={{
              background: colorB,
              color: 'var(--ink)',
              padding: '10px 12px',
              textAlign: 'right',
            }}
          >
            <div className="eyebrow" style={{ color: 'inherit' }}>
              {battle.totals.claimsB} claims
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, lineHeight: 1 }}>
              {battle.squadB.name}
            </div>
            <div className="body-sm">{battle.totals.drinksB} drinks</div>
          </div>
        </div>

        {/* Route mode pill */}
        <div className="row" style={{ justifyContent: 'center' }}>
          <span className="chip" style={{ background: 'var(--paper-warm)' }}>
            {battle.routeType === 'crawl' ? 'bar crawl · 4 stops' : 'freestyle · anywhere'}
          </span>
        </div>

        {/* Per-venue list */}
        <div
          className="stack-sm"
          style={{ overflowY: 'auto', alignContent: 'start' }}
          data-testid="battle-venues-list"
        >
          {battle.venues.length === 0 ? (
            <p className="body-sm muted" style={{ textAlign: 'center', marginTop: 12 }}>
              No stops yet. Check in at a bar and log drinks — first venue auto-joins the battle.
            </p>
          ) : (
            battle.venues.map((v) => {
              const claimedBy =
                v.claimedBy === battle.squadA.id
                  ? battle.squadA
                  : v.claimedBy === battle.squadB.id
                    ? battle.squadB
                    : null
              const claimedColor = claimedBy ? colorVar(claimedBy.color) : 'var(--paper-warm)'
              return (
                <div
                  key={v.venueId}
                  className="card"
                  style={{
                    background: 'var(--paper)',
                    border: claimedBy ? `3px solid ${claimedColor}` : '2.5px solid var(--line)',
                    padding: 12,
                    display: 'grid',
                    gap: 6,
                  }}
                  data-testid={`battle-venue-${v.venueId}`}
                >
                  <div className="row-between">
                    <div
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 20,
                        lineHeight: 1,
                      }}
                    >
                      {v.venueName}
                    </div>
                    {claimedBy ? (
                      <span
                        className="chip"
                        style={{ background: claimedColor, color: 'var(--ink)' }}
                      >
                        claimed · {claimedBy.name}
                      </span>
                    ) : (
                      <span className="chip" style={{ background: 'var(--paper-warm)' }}>
                        unclaimed
                      </span>
                    )}
                  </div>
                  <div
                    className="row"
                    style={{ gap: 8, justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <span
                      className="body-sm"
                      style={{ color: 'var(--ink)' }}
                      data-testid={`battle-venue-${v.venueId}-tally`}
                    >
                      <strong style={{ color: colorA }}>{battle.squadA.name}</strong> {v.countA}
                      <span style={{ margin: '0 6px' }}>·</span>
                      <strong style={{ color: colorB }}>{battle.squadB.name}</strong> {v.countB}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleClaim(v.venueId)}
                      disabled={claiming === v.venueId}
                      data-testid={`battle-claim-${v.venueId}`}
                      style={{
                        minHeight: 36,
                        padding: '0 14px',
                        border: '2px solid var(--line)',
                        borderRadius: 999,
                        background: 'var(--ink)',
                        color: 'var(--paper)',
                        fontWeight: 800,
                        fontSize: 12,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {claiming === v.venueId ? '…' : 'claim'}
                    </button>
                  </div>
                </div>
              )
            })
          )}
        </div>

        {toast ? (
          <output className="toast" data-testid="battle-toast">
            {toast}
          </output>
        ) : null}

        {/* Bottom: share / end */}
        <div className="row" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn secondary full"
            onClick={() => {
              if (typeof navigator !== 'undefined' && navigator.clipboard) {
                void navigator.clipboard.writeText(battle.id)
                setToast('Battle id copied.')
              }
            }}
            style={{ minHeight: 48 }}
          >
            Share id
          </button>
          <button
            type="button"
            className="btn full"
            onClick={() => navigate({ to: '/' })}
            style={{ minHeight: 48 }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
